import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid2,
  Typography,
  Button,
  Chip,
  Stack,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CardTile from "../elements/CardTile";
import FlightIcon from "@mui/icons-material/Flight";
import PetsIcon from "@mui/icons-material/Pets";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VerifiedIcon from "@mui/icons-material/Verified";
import StarIcon from "@mui/icons-material/Star";
import BoltIcon from "@mui/icons-material/Bolt";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useSelector } from "react-redux";
import { useTheme, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { setInsuranceType } from "../../redux/actions";
import AppBarComponent from "../elements/AppBarComponent";
import Footer from "../elements/Footer";

import ResponsiveImage from "../elements/ResponsiveImage";
import { backgroundImageSx, SIZES } from "../../utils/responsiveImage";

function Homepage() {
  const navigate = useNavigate();
  const [isAppBarFixed, setIsAppBarFixed] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const scrollToSectionRef = useRef(null);

  const currentAgentId = useSelector((state) => state.agent.currentAgentId);
  const dispatch = useDispatch();

  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const tiles = [
    {
      image: "/assets/images/travel-2.png",
      icon: <FlightIcon />,
      title: "Travel Insurance",
      description: "Worldwide medical, baggage and trip cancellation cover.",
      navigateTo: "/travel-landing",
    },
    {
      image: "/assets/images/pet.png",
      icon: <PetsIcon />,
      title: "Pet Insurance",
      description:
        "Vet bills, surgery and accidental cover for your companion.",
      navigateTo: "/pet-landing",
    },
    {
      image: "/assets/images/motor.png",
      icon: <DriveEtaIcon />,
      title: "Motor Insurance",
      description: "Comprehensive and third-party plans for every driver.",
      navigateTo: "/motor-landing",
    },
    {
      image: "/assets/images/life.png",
      icon: <VerifiedUserIcon />,
      title: "Life Insurance",
      description: "Long-term protection for the people who depend on you.",
      navigateTo: "/coming-soon?product=life",
    },
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  useEffect(() => {
    const loggedIn = !!currentAgentId;
    setIsLoggedIn(loggedIn);

    const handleScroll = () => {
      if (!isXsScreen) {
        setIsAppBarFixed(window.scrollY > 20);
      } else {
        setIsAppBarFixed(true);
      }
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [currentAgentId, isXsScreen]);

  const handleTileClick = (path, insuranceType) => {
    dispatch(setInsuranceType(insuranceType));
    navigate(path);
  };

  const handleScrollToSection = () => {
    const headerHeight = 80;
    window.scrollTo({
      top: scrollToSectionRef.current?.offsetTop - headerHeight,
      behavior: "smooth",
    });
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "100vw",
          height: "100vh",
          ...backgroundImageSx("/assets/images/homepage.jpg"),
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Cinematic gradient overlay (replaces flat black 50%) */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(120deg, rgba(20,14,8,0.78) 0%, rgba(20,14,8,0.45) 45%, rgba(20,14,8,0.20) 100%)",
            zIndex: 1,
          }}
        />
        <AppBarComponent
          isAppBarFixed={isAppBarFixed}
          isLoggedIn={isLoggedIn}
          isXsScreen={isXsScreen}
          navigate={navigate}
          tiles={tiles}
        />
        {/* Hero Section */}
        <Box
          sx={{
            position: "absolute",
            top: { xs: "18%", md: "22%" },
            left: { xs: 0, md: "5%" },
            right: { xs: 0, md: "auto" },
            px: 3,
            zIndex: 2,
            maxWidth: { xs: "100%", md: "60%" },
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <Chip
              icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
              label="Central Bank of UAE Registered • Reg. 22"
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.12)",
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.75rem",
                letterSpacing: 0.6,
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.25)",
                "& .MuiChip-icon": { color: "#C9A777" },
                mb: 2.75,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                color: "#fff",
                fontWeight: 700,
                textShadow: "2px 2px 14px rgba(0,0,0,0.45)",
                fontSize: { xs: "2.85rem", md: "4.75rem" },
                letterSpacing: "-0.03em",
                lineHeight: 1.02,
                mb: 2.25,
              }}
            >
              Get an instant
              <br />
              insurance quote.
              <Box component="span" sx={{ color: "#E5C690" }}>
                {" "}
                Compare.
              </Box>
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.88)",
                fontSize: { xs: "1.0625rem", md: "1.25rem" },
                fontWeight: 400,
                letterSpacing: "-0.005em",
                maxWidth: 620,
                mb: 3.5,
                lineHeight: 1.6,
              }}
            >
              Side-by-side quotes from a curated panel of trusted insurers
              across Travel, Pet, Motor and Life. No paperwork, no calls — done
              in under 90 seconds.
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                background:
                  "linear-gradient(135deg, #d3b48a 0%, #C9A777 50%, #b4966b 100%)",
                color: "#fff",
                fontSize: "1rem",
                fontWeight: 600,
                letterSpacing: 0.3,
                textTransform: "none",
                px: 4,
                py: 1.5,
                borderRadius: "999px",
                boxShadow: "0 18px 40px rgba(201,167,119,0.45)",
                ":hover": {
                  background:
                    "linear-gradient(135deg, #C9A777 0%, #b4966b 50%, #a1885e 100%)",
                  boxShadow: "0 22px 50px rgba(201,167,119,0.55)",
                },
              }}
              onClick={handleScrollToSection}
            >
              Get an instant quote
            </Button>

            {/* Trust micro-stats */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1.5, sm: 4 }}
              sx={{ mt: 5 }}
            >
              {[
                {
                  icon: <StarIcon sx={{ color: "#E5C690" }} />,
                  label: "Customer-first experience",
                },
                {
                  icon: <BoltIcon sx={{ color: "#E5C690" }} />,
                  label: "Fast quote-to-cover",
                },
                {
                  icon: <HandshakeOutlinedIcon sx={{ color: "#E5C690" }} />,
                  label: "Backed by trusted insurers",
                },
              ].map((s, i) => (
                <Stack key={i} direction="row" alignItems="center" spacing={1}>
                  {s.icon}
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.92)",
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      letterSpacing: 0.1,
                    }}
                  >
                    {s.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </motion.div>
        </Box>

        {/* Scroll indicator */}
        <Box
          sx={{
            position: "absolute",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
            color: "rgba(255,255,255,0.85)",
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{ letterSpacing: 1.8, fontWeight: 700, fontSize: "0.7rem" }}
          >
            EXPLORE
          </Typography>
          <IconButton
            onClick={handleScrollToSection}
            sx={{
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.3)",
              animation: "bounce 2s infinite",
              "@keyframes bounce": {
                "0%,100%": { transform: "translateY(0)" },
                "50%": { transform: "translateY(6px)" },
              },
            }}
          >
            <KeyboardArrowDownIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Section heading */}
      <Box
        ref={scrollToSectionRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#fff",
          minHeight: { xs: "20vh", sm: "25vh" },
          padding: { xs: 3, md: 6 },
          textAlign: "center",
        }}
      >
        <Chip
          label="OUR PRODUCTS"
          size="small"
          sx={{
            bgcolor: "#fdf6ea",
            color: "#a1885e",
            fontWeight: 700,
            fontSize: "0.72rem",
            letterSpacing: 1.6,
            border: "1px solid rgba(201,167,119,0.30)",
            mb: 2.25,
          }}
        />
        <Typography
          variant="h3"
          sx={{
            color: "#3d322b",
            fontWeight: 700,
            fontSize: { xs: "2rem", md: "3.25rem" },
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
          }}
        >
          What are you{" "}
          <Box component="span" sx={{ color: "#C9A777" }}>
            looking for?
          </Box>
        </Typography>
        <Typography
          sx={{
            color: "#7B6E66",
            mt: 1.5,
            maxWidth: 620,
            fontSize: { xs: "1rem", md: "1.125rem" },
            lineHeight: 1.65,
          }}
        >
          Pick a category to compare quotes from leading insurers in seconds.
        </Typography>
      </Box>

      {/* Tiles section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background:
            "linear-gradient(180deg, #FBF1E2 0%, #fdf6ea 50%, #FBF1E2 100%)",
        }}
      >
        <Grid2
          container
          spacing={{ xs: 5, md: 8 }}
          sx={{
            py: { xs: 5, md: 10 },
            px: { xs: 3, md: 20 },
          }}
        >
          {tiles.map((tile, index) => (
            <Grid2 key={index} size={{ xs: 12, md: 6 }}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{
                  duration: 0.7,
                  ease: "easeOut",
                  delay: index * 0.05,
                }}
                variants={cardVariants}
              >
                <CardTile
                  {...tile}
                  onArrowClick={() =>
                    handleTileClick(tile.navigateTo, tile.title)
                  }
                />
              </motion.div>
            </Grid2>
          ))}
        </Grid2>
      </Box>

      {/* Why us strip */}
      <Box
        sx={{ bgcolor: "#fff", py: { xs: 6, md: 10 }, px: { xs: 3, md: 10 } }}
      >
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Chip
            label="WHY SMARTSURE"
            size="small"
            sx={{
              bgcolor: "#fdf6ea",
              color: "#a1885e",
              fontWeight: 700,
              fontSize: "0.72rem",
              letterSpacing: 1.6,
              border: "1px solid rgba(201,167,119,0.30)",
              mb: 2.25,
            }}
          />
          <Typography
            variant="h3"
            sx={{
              color: "#3d322b",
              fontWeight: 700,
              fontSize: { xs: "1.75rem", md: "2.75rem" },
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
            }}
          >
            Built for clarity.{" "}
            <Box component="span" sx={{ color: "#C9A777" }}>
              Designed for speed.
            </Box>
          </Typography>
        </Box>
        <Grid2 container spacing={4} sx={{ maxWidth: 960, mx: "auto" }}>
          {[
            {
              icon: <BoltIcon />,
              title: "Quotes in 90 seconds",
              desc: "Tell us what you need — get a side-by-side comparison instantly.",
            },
            {
              icon: <VerifiedIcon />,
              title: "Regulated & secure",
              desc: "Central Bank of UAE registered, with bank-grade encryption.",
            },
            {
              icon: <HeadsetMicOutlinedIcon />,
              title: "24/7 claims support",
              desc: "Real humans on call when it matters most. Day or night.",
            },
          ].map((item, i) => (
            <Grid2 key={i} size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  p: 3.5,
                  height: "100%",
                  borderRadius: 4,
                  bgcolor: "#fff",
                  border: "1px solid rgba(123,110,102,0.10)",
                  boxShadow: "0 6px 18px rgba(123,110,102,0.06)",
                  transition: "all .3s ease",
                  ":hover": {
                    transform: "translateY(-4px)",
                    borderColor: "rgba(201,167,119,0.40)",
                    boxShadow: "0 14px 32px rgba(201,167,119,0.18)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, #fdf6ea 0%, #f0dfbb 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#a1885e",
                    mb: 2.5,
                  }}
                >
                  {React.cloneElement(item.icon, { sx: { fontSize: 28 } })}
                </Box>
                <Typography
                  sx={{
                    color: "#3d322b",
                    fontWeight: 700,
                    fontSize: "1.125rem",
                    letterSpacing: "-0.01em",
                    lineHeight: 1.3,
                    mb: 0.75,
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    color: "#7B6E66",
                    fontSize: "0.9375rem",
                    lineHeight: 1.65,
                  }}
                >
                  {item.desc}
                </Typography>
              </Box>
            </Grid2>
          ))}
        </Grid2>
      </Box>

      {/* Partners section */}
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#fff",
            minHeight: { xs: "12vh", sm: "20vh" },
            padding: { xs: 2, md: 0 },
            textAlign: "center",
          }}
        >
          <Chip
            label="OUR PARTNERS"
            size="small"
            sx={{
              bgcolor: "#fdf6ea",
              color: "#a1885e",
              fontWeight: 700,
              fontSize: "0.72rem",
              letterSpacing: 1.6,
              border: "1px solid rgba(201,167,119,0.30)",
              mb: 2.25,
            }}
          />
          <Typography
            variant="h3"
            sx={{
              color: "#3d322b",
              fontWeight: 700,
              fontSize: { xs: "1.75rem", md: "2.75rem" },
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
            }}
          >
            Insurers you can trust
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#fff",
            pb: { xs: 5, md: 10 },
            pt: { xs: 3, md: 4 },
          }}
        >
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            variants={cardVariants}
          >
            <Grid2
              container
              spacing={{ xs: 3, md: 4 }}
              sx={{
                px: { xs: 3, md: 20 },
                justifyContent: "center",
              }}
            >
              {[
                "company1.png",
                "company2.png",
                "company3.png",
                "company4.png",
                "company5.png",
                "company6.png",
                "company7.png",
              ].map((logo, index) => (
                <Grid2 size={{ xs: 6, md: 3 }} key={index}>
                  <Box
                    sx={{
                      width: "100%",
                      height: 140,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      bgcolor: "#fff",
                      border: "1px solid rgba(123,110,102,0.10)",
                      borderRadius: 3,
                      boxShadow: "0 4px 14px rgba(123,110,102,0.05)",
                      transition: "all .3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        borderColor: "rgba(201,167,119,0.40)",
                        boxShadow: "0 14px 32px rgba(201,167,119,0.18)",
                      },
                    }}
                  >
                    <ResponsiveImage
                      src={`/assets/images/${logo}`}
                      alt={`Company ${index + 1}`}
                      sizes={SIZES.brand}
                      sx={{
                        maxWidth: "75%",
                        maxHeight: "70%",
                        objectFit: "contain",
                        filter: "saturate(.95)",
                      }}
                    />
                  </Box>
                </Grid2>
              ))}
            </Grid2>
          </motion.div>
        </Box>
      </Box>

      <Footer />
    </>
  );
}

export default Homepage;
